import { useMelwaysInfo } from '@seek/melways-react';
import { type Country, getHostName } from '@seek/melways-sites';
import { generateMetaRobots } from '@seek/seek-jobs-seo';
import { useTranslations } from '@vocab/react';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';

import type { SafetyHelmetProps } from 'src/components/SafetyHelmet/SafetyHelmet';
import { useLocalisedLinks } from 'src/utils/melwaysHelper';

import { generateMetaDescription, generateMetaTitle } from '../utils';

import translations from './.vocab';
import { useDescriptionContent } from './description/content';
import { useLocalisedHrefLangLinks } from './hreflang/hreflang';
import { usePoeaHrefLangLinks } from './hreflang/poeaHreflang';
import { useDescriptiveTitle } from './title';

interface SharedHeadInput {
  canonicalUrl: string | null;
  query: any;
  isZeroResults: boolean;
  pathname: string;
  hasExternalJobs: boolean;
  country: Country;
  isExpired: boolean;
  locationType?: string;
}

const useSchema = () => {
  const { site } = useMelwaysInfo();

  const hostname = getHostName({
    site,
    staging: false,
  });

  const url = `https://${hostname}`;

  return {
    '@context': 'http://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        url,
        potentialAction: {
          '@type': 'SearchAction',
          target: `${url}/{search_term_string}-jobs`,
          'query-input': 'required name=search_term_string',
        },
      },
    ],
  };
};

/* Provides meta, script & link tags to be used as a base for pages on seek.
 * Note that these tags are over-written by `useHomePageHead`, `useJobDetailsHead` & `useSearchResultsHead`
 * @param {SharedHeadInput}
 */
export const useSharedHead = (
  sharedMetaInput: SharedHeadInput,
): SafetyHelmetProps => {
  const {
    canonicalUrl,
    query = {},
    isZeroResults,
    pathname = '/',
    hasExternalJobs = false,
    country,
    isExpired,
    locationType = '',
  } = sharedMetaInput || {};

  const metaRobotsContent = generateMetaRobots({
    path: pathname,
    query,
    noResults: isZeroResults,
    hasExternalJobs,
    country,
    isExpired,
    locationType,
  });

  const pageTitle = useDescriptiveTitle(pathname);
  const descriptionContent = useDescriptionContent();
  const { t } = useTranslations(translations);

  const title = t('shared-title');
  const schema = useSchema();

  const tags: SafetyHelmetProps = {
    title: pageTitle || title,
    meta: [],
    link: [],
    script: [],
  };

  const localisedCanonicalUrl = useLocalisedLinks({
    path: canonicalUrl || '',
    absolute: true,
  });

  tags.meta = [
    { name: 'robots', content: metaRobotsContent },
    ...generateMetaTitle(title),
    ...(canonicalUrl
      ? [{ property: 'og:url', content: localisedCanonicalUrl }]
      : []),
    ...generateMetaDescription(descriptionContent),
  ].filter((item) => !isEmpty(item));

  tags.link =
    canonicalUrl && !includes(metaRobotsContent, 'noindex')
      ? [{ rel: 'canonical', href: localisedCanonicalUrl }]
      : [];

  const alternateLinks = useLocalisedHrefLangLinks();
  const poeaAlternateLinks = usePoeaHrefLangLinks();

  tags.link = tags.link.concat(
    poeaAlternateLinks.length > 0 ? poeaAlternateLinks : alternateLinks,
  );

  tags.script = [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify(schema),
    },
  ];

  return tags;
};
